import React, { createContext, useCallback, useContext, useEffect, useState } from 'react';

const ColorModeContext = createContext({
  mode: 'light',
  toggleMode: () => {},
  setMode: () => {},
});

/**
 * Proveedor global de modo de color (light/dark).
 *
 * - Mantiene el estado único `mode` para toda la aplicación.
 * - Sincroniza `document.documentElement[data-theme]` y `localStorage` con `sysgago-theme`.
 * - Permite re-renderizar todos los consumidores (incluyendo App/ThemeProvider y componentes MUI)
 *   instantáneamente sin necesidad de recargar la página.
 */
export function ColorModeProvider({ children }) {
  // Estado inicial: leemos directamente del DOM (el anti-FOUC ya lo pobló).
  const [mode, setModeState] = useState(() => {
    if (typeof document === 'undefined') return 'light';
    return document.documentElement.getAttribute('data-theme') === 'dark'
      ? 'dark'
      : 'light';
  });

  // Mantener <html data-theme> en sync con el state de React.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', mode);
    try {
      localStorage.setItem('sysgago-theme', mode);
    } catch {
      // localStorage no disponible (modo privado / SSR) — no romper.
    }
  }, [mode]);

  // Si nunca hubo preferencia manual, escuchar cambios del sistema.
  useEffect(() => {
    let stored = null;
    try {
      stored = localStorage.getItem('sysgago-theme');
    } catch {
      // ignore
    }
    if (stored) return undefined; // el usuario ya eligió, no seguir al sistema

    if (typeof window === 'undefined' || !window.matchMedia) return undefined;
    const mq = window.matchMedia('(prefers-color-scheme: dark)');
    const onChange = (e) => setModeState(e.matches ? 'dark' : 'light');
    // addEventListener es la API moderna; fallback a addListener para Safari viejo.
    if (mq.addEventListener) mq.addEventListener('change', onChange);
    else if (mq.addListener) mq.addListener(onChange);
    return () => {
      if (mq.removeEventListener) mq.removeEventListener('change', onChange);
      else if (mq.removeListener) mq.removeListener(onChange);
    };
  }, []);

  const toggleMode = useCallback(() => {
    setModeState((prev) => (prev === 'dark' ? 'light' : 'dark'));
  }, []);

  const setMode = useCallback((m) => {
    if (m !== 'light' && m !== 'dark') return;
    setModeState(m);
  }, []);

  return (
    <ColorModeContext.Provider value={{ mode, toggleMode, setMode }}>
      {children}
    </ColorModeContext.Provider>
  );
}

/**
 * Hook de modo de color (light/dark).
 *
 * API:
 *   const { mode, toggleMode, setMode } = useColorMode();
 *   - mode: 'light' | 'dark'
 *   - toggleMode(): alterna entre light y dark
 *   - setMode(m): setea explícitamente ('light' | 'dark')
 */
export default function useColorMode() {
  return useContext(ColorModeContext);
}
